import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Grid,
  Paper,
  IconButton,
  Divider,
  Alert,
  Card,
  CardContent,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogTitle,
  Avatar,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import AppLayout from "../../components/AppLayout";
import ApplySchemeCreditDialog from "../../components/ApplySchemeCreditDialog";
import API_BASE_URL from "../../config/api";


// Inline suggestion dropdown for customer search
// Inline suggestion dropdown for customer search
function SuggestionDropdown({ suggestions, onSelect, highlightedIndex }) {
  const itemRefs = useRef([]);

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  return (
    <Paper
      elevation={8}
      sx={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        zIndex: 1300,
        mt: 0.5,
        bgcolor: "#1e1e1e",
        border: "1px solid rgba(196,165,116,0.35)",
        borderRadius: 1,
        maxHeight: 260,
        overflowY: "auto",
      }}
    >
      {suggestions.map((c, i) => (
        <Box
          key={c.TokenNo ?? i}
          ref={(el) => (itemRefs.current[i] = el)}
          onMouseDown={() => onSelect(c)}
          sx={{
            px: 2,
            py: 1.2,
            cursor: "pointer",
            borderBottom: "1px solid rgba(255,255,255,0.04)",
            bgcolor: highlightedIndex === i ? "rgba(196,165,116,0.2)" : "transparent",
            "&:hover": { bgcolor: "rgba(196,165,116,0.12)" },
            "&:last-child": { borderBottom: "none" },
          }}
        >
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem", lineHeight: 1.3 }}>
            {c.CustomerName}
          </Typography>
          <Typography sx={{ color: "rgba(196,165,116,0.85)", fontSize: "0.78rem", mt: 0.2 }}>
            Token: {c.TokenNo ?? "—"} &nbsp;·&nbsp; {c.PhoneNumber || "No phone"}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

export default function CreateBill() {
  const navigate = useNavigate();
  const billedBy = localStorage.getItem("fullName") || "Unknown User";

  // Dark theme TextField styling
  const TextFieldStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: 1,
      "& fieldset": {
        borderColor: "rgba(196, 165, 116, 0.3)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(196, 165, 116, 0.5)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#C4A574",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.7)",
      "&.Mui-focused": {
        color: "#C4A574",
      },
    },
    "& .MuiInputBase-input": {
      color: "#fff",
      "&::placeholder": {
        color: "rgba(255, 255, 255, 0.5)",
        opacity: 1,
      },
    },
    "& .MuiInputBase-input:disabled": {
      color: "#fff",
      WebkitTextFillColor: "#fff",
    },
  };

  const [billData, setBillData] = useState({
    tokenNo: "",
    customerName: "",
    contact: "",
    advance: "",
    wasCustomizationGood: "",
    orderDescription: "",
    dueDate: "",
    discountType: "item", // 'item' or 'bill'
    billDiscountPercent: "", // Bill-level discount percentage
    couponCode: "", // Auto-generated coupon code
  });

  const [customerFetched, setCustomerFetched] = useState(false); // Track if customer was fetched from DB
  const [customerSearch, setCustomerSearch] = useState([]); // Store customer search results
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false); // Track if customer fetch is in progress
  const [customerSuggestions, setCustomerSuggestions] = useState([]); // Autocomplete suggestions
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [activeSearchField, setActiveSearchField] = useState(null); // 'token' | 'name' | 'phone'
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const suggestionsDebounceRef = useRef(null);

  const [items, setItems] = useState([
    {
      id: Date.now(),
      productCode: "",
      sizes: [
        { 
          size: "", 
          quantity: "", 
          unitPrice: "",
          discount: "",
          alterations: [{ description: "", cost: "", materialId: "", quantityUsed: "" }],
          stitching: [{ description: "", cost: "", materialId: "", quantityUsed: "" }]
        }
      ],
    },
  ]);

  const [errors, setErrors] = useState({});
  const [calculations, setCalculations] = useState({
    subTotal: 0,
    totalTax: 0,
    totalDiscount: 0,
    totalAmount: 0,
    balance: 0,
  });

  const [selectedProducts, setSelectedProducts] = useState({}); // Store selected product details by item.id
  const [productSearch, setProductSearch] = useState({});
  const [imagePreview, setImagePreview] = useState({ open: false, url: "" });
  const [rawMaterials, setRawMaterials] = useState([]); // Store raw materials for customizations

  const [appliedSchemeCredit, setAppliedSchemeCredit] = useState(0);
  const [availableSchemeCredit, setAvailableSchemeCredit] = useState(0);
  const [schemeCreditDialogOpen, setSchemeCreditDialogOpen] = useState(false);

  
  // Coupon state
  const [availableCoupon, setAvailableCoupon] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(0);

  // Ref to track the last calculated coupon amount to prevent re-rendering loops
  const lastCalculatedCouponRef = useRef(0);

  const sizes = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];

  useEffect(() => {
    calculateTotals();
  }, [items, billData.advance, billData.discountType, billData.billDiscountPercent, appliedSchemeCredit, appliedCoupon]);

  // Auto-apply scheme credit when total amount changes - dynamically adjust the scheme credit
  useEffect(() => {
    if (customerFetched && availableSchemeCredit > 0 && parseFloat(calculations.totalAmount) > 0) {
      const optimalAmount = Math.min(availableSchemeCredit, parseFloat(calculations.totalAmount));
      // Only update if the optimal amount is different from current applied amount
      if (optimalAmount !== appliedSchemeCredit) {
        setAppliedSchemeCredit(optimalAmount);
      }
    } else if (customerFetched && parseFloat(calculations.totalAmount) <= 0) {
      // Reset scheme credit if total amount becomes 0 or negative
      setAppliedSchemeCredit(0);
    }
  }, [calculations.totalAmount, availableSchemeCredit, customerFetched]);

  // Auto-apply coupon when customer is loaded and total amount changes
  useEffect(() => {
    // Add debouncing to prevent excessive updates
    const timeoutId = setTimeout(() => {
      if (customerFetched && availableCoupon > 0) {
        const totalAfterDiscount = parseFloat(calculations.subTotal) - parseFloat(calculations.totalDiscount);
        const balanceBeforeCoupon = totalAfterDiscount - (parseFloat(billData.advance) || 0) - appliedSchemeCredit;
        
        // Simple logic: Apply the minimum of available coupon or balance
        let optimalCouponAmount = 0;
        if (balanceBeforeCoupon > 0) {
          optimalCouponAmount = Math.min(availableCoupon, balanceBeforeCoupon);
        }
        
        // Always update if the optimal amount is different from current applied amount
        const currentApplied = appliedCoupon;
        
        if (Math.abs(optimalCouponAmount - currentApplied) > 0.01) {
          // console.log(`🎫 Coupon auto-apply: Available: ₹${availableCoupon}, Balance: ₹${balanceBeforeCoupon}, Applying: ₹${optimalCouponAmount} (was: ₹${currentApplied})`);
          setAppliedCoupon(optimalCouponAmount);
        }
      } else if (customerFetched && (availableCoupon <= 0 || parseFloat(calculations.subTotal) <= 0)) {
        // Reset coupon if no coupon available or no items
        if (appliedCoupon > 0) {
          // console.log(`🎫 Resetting coupon to 0`);
          setAppliedCoupon(0);
        }
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [calculations.subTotal, calculations.totalDiscount, billData.advance, appliedSchemeCredit, availableCoupon, customerFetched]);

  useEffect(() => {
    fetchRawMaterials();
  }, []);

  // Removed fetchAllProducts - we now search on-demand for better performance

  const fetchRawMaterials = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/inventory/raw-materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setRawMaterials(data.materials || []);
      }
    } catch (error) {
      console.error("Error fetching raw materials:", error);
    }
  };

  const handleProductSearch = async (itemId, searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/inventory/products/search/${encodeURIComponent(searchValue)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProductSearch((prev) => ({ ...prev, [itemId]: data.products || [] }));
      }
    } catch (error) {
      console.error("Error searching products:", error);
    }
  };

  const handleProductSelect = async (itemId, product) => {
    if (!product) return;

    // If product is a string (manual input), fetch product details
    if (typeof product === 'string') {
      setItems(items.map((item) => 
        item.id === itemId ? { ...item, productCode: product } : item
      ));
      
      // Try to fetch product details for the manually entered code
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/api/inventory/products/search/${encodeURIComponent(product)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.ok) {
          const data = await response.json();
          const foundProduct = data.products?.find(p => p.ProductCode === product);
          if (foundProduct) {
            setSelectedProducts(prev => ({ ...prev, [itemId]: foundProduct }));
          }
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
      }
      return;
    }

    // Store the selected product details
    setSelectedProducts(prev => ({ ...prev, [itemId]: product }));

    setItems(
      items.map((item) => {
        if (item.id === itemId) {
          // Auto-fill first available size and quantity 1
          const firstSize = product.sizes && product.sizes.length > 0 ? product.sizes[0].Size : "";
          
          const updatedItem = { 
            ...item, 
            productCode: product.ProductCode,
            sizes: [{
              size: firstSize,
              quantity: "1",
              unitPrice: product.Price || "",
              discount: "",
              alterations: [{ description: "", cost: "" }],
              stitching: [{ description: "", cost: "" }],
            }]
          };
          
          return updatedItem;
        }
        return item;
      })
    );
  };

  const handleImagePreview = (url) => {
    setImagePreview({ open: true, url });
  };

  const closeImagePreview = () => {
    setImagePreview({ open: false, url: "" });
  };

  const calculateTotals = () => {
    let subTotal = 0;
    let itemLevelDiscount = 0;
    let totalAlterations = 0;
    let totalStitching = 0;

    items.forEach((item) => {
      item.sizes.forEach((sizeItem) => {
        const sizeSubTotal = (parseFloat(sizeItem.quantity) || 0) * (parseFloat(sizeItem.unitPrice) || 0);
        
        const alterationsTotal = sizeItem.alterations.reduce(
          (sum, a) => sum + (parseFloat(a.cost) || 0),
          0
        );
        const stitchingTotal = sizeItem.stitching.reduce(
          (sum, s) => sum + (parseFloat(s.cost) || 0),
          0
        );

        totalAlterations += alterationsTotal;
        totalStitching += stitchingTotal;

        // SubTotal should be BEFORE discount
        const sizeTotal = sizeSubTotal + alterationsTotal + stitchingTotal;
        subTotal += sizeTotal;
        
        // Only add item-level discounts if discount type is 'item'
        if (billData.discountType === 'item') {
          itemLevelDiscount += (parseFloat(sizeItem.discount) || 0);
        }
      });
    });

    // Calculate total discount based on discount type
    let totalDiscount = 0;
    if (billData.discountType === 'bill') {
      // Calculate discount from percentage
      const discountPercent = parseFloat(billData.billDiscountPercent) || 0;
      totalDiscount = (subTotal * discountPercent) / 100;
    } else {
      totalDiscount = itemLevelDiscount;
    }

    // Total Amount is SubTotal minus Discount minus Coupon
    const totalAmount = subTotal - totalDiscount - appliedCoupon;
    const balance = totalAmount - (parseFloat(billData.advance) || 0) - appliedSchemeCredit;

    setCalculations({
      subTotal: subTotal.toFixed(2),
      totalTax: "0.00",
      totalDiscount: totalDiscount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      balance: balance.toFixed(2),
      totalAlterations: totalAlterations.toFixed(2),
      totalStitching: totalStitching.toFixed(2),
    });
  };

  // Handle scheme credit application success
  const handleSchemeCreditApplied = (data) => {
    // Update applied scheme credit from dialog response
    setAppliedSchemeCredit(data.appliedAmount || 0);
    
    // Update available credit (subtract applied amount)
    setAvailableSchemeCredit(data.remainingCredit || 0);
    
    toast.success(`Scheme credit of ₹${(data.appliedAmount || 0).toFixed(2)} will be applied to this bill`);
  };

  // Auto-apply optimal scheme credit amount
  const autoApplySchemeCredit = () => {
    if (availableSchemeCredit > 0 && parseFloat(calculations.totalAmount) > 0) {
      const optimalAmount = Math.min(availableSchemeCredit, parseFloat(calculations.totalAmount));
      setAppliedSchemeCredit(optimalAmount);
      toast.success(`Auto-applied scheme credit: ₹${optimalAmount.toFixed(2)}`);
    }
  };

  // Auto-apply optimal coupon amount
  const autoApplyCoupon = () => {
    if (availableCoupon > 0 && parseFloat(calculations.totalAmount) > 0) {
      const totalAfterDiscount = parseFloat(calculations.subTotal) - parseFloat(calculations.totalDiscount);
      const balanceBeforeCoupon = totalAfterDiscount - (parseFloat(billData.advance) || 0) - appliedSchemeCredit;
      
      let optimalAmount = 0;
      if (balanceBeforeCoupon > 0) {
        if (availableCoupon > balanceBeforeCoupon) {
          // Coupon is more than bill balance - apply only what's needed to cover the balance
          optimalAmount = balanceBeforeCoupon;
        } else {
          // Coupon is less than or equal to bill balance - apply full coupon
          optimalAmount = availableCoupon;
        }
      }
      
      if (optimalAmount > 0) {
        lastCalculatedCouponRef.current = optimalAmount;
        setAppliedCoupon(optimalAmount);
        toast.success(`Auto-applied coupon: ₹${optimalAmount.toFixed(2)}`);
      }
    }
  };

  const handleBillDataChange = (e) => {
    const { name, value } = e.target;
    setBillData({ ...billData, [name]: value });
  };

  // Fetch available scheme credit for customer
  const fetchAvailableSchemeCredit = async (tokenNo) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/schemes/customer/${tokenNo}/available-credit`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailableSchemeCredit(data.totalAvailable || 0);
      } else {
        setAvailableSchemeCredit(0);
      }
    } catch (error) {
      console.error("Error fetching scheme credit:", error);
      setAvailableSchemeCredit(0);
    }
  };

  // Fetch customer's available coupon
  const fetchCustomerCoupon = async (tokenNo) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/customers/${tokenNo}/coupon`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailableCoupon(parseFloat(data.couponAmount) || 0);
      } else {
        setAvailableCoupon(0);
      }
    } catch (error) {
      console.error("Error fetching customer coupon:", error);
      setAvailableCoupon(0);
    }
  };

  // Search customers for autocomplete suggestions (name, phone, token)
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setCustomerSuggestions([]);
      setIsSuggestionsOpen(false);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/customers/search?q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCustomerSuggestions(data.customers || []);
        setIsSuggestionsOpen((data.customers || []).length > 0);
        setHighlightedIndex(-1);
      }
    } catch (error) {
      console.error("Customer search error:", error);
    }
  }, []);

  // Fetch customer by Token No
  const fetchCustomerByToken = async (tokenNo) => {
    if (!tokenNo || isLoadingCustomer) return;
    
    try {
      setIsLoadingCustomer(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/customers/token/${tokenNo}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setBillData(prev => ({
          ...prev,
          customerName: result.data.CustomerName,
          contact: result.data.PhoneNumber,
          couponCode: result.data.CouponCode || null,
        }));
        setCustomerFetched(true);
        
        // Fetch available scheme credit and coupon
        await fetchAvailableSchemeCredit(tokenNo);
        await fetchCustomerCoupon(tokenNo);
        
        toast.success("Customer details loaded!");
        
        // Auto-apply scheme credit and coupon after a short delay to ensure calculations are updated
        setTimeout(() => {
          autoApplySchemeCredit();
          autoApplyCoupon();
        }, 100);
      } else {
        toast.error("Customer not found with this Token No");
        setCustomerFetched(false);
        setAvailableSchemeCredit(0);
        setBillData(prev => ({
          ...prev,
          customerName: "",
          contact: "",
        }));
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      toast.error("Failed to fetch customer details");
      setCustomerFetched(false);
      setAvailableSchemeCredit(0);
    } finally {
      setIsLoadingCustomer(false);
    }
  };

  // Called when user picks a suggestion from the dropdown
  const selectCustomerSuggestion = async (customer) => {
    setIsSuggestionsOpen(false);
    setHighlightedIndex(-1);
    setCustomerSuggestions([]);
    setBillData(prev => ({
      ...prev,
      tokenNo: String(customer.TokenNo),
      customerName: customer.CustomerName,
      contact: customer.PhoneNumber || "",
    }));
    // Fetch full details (scheme credit, coupon, etc.)
    await fetchCustomerByToken(String(customer.TokenNo));
  };

  // Fetch customer by Phone Number
  const fetchCustomerByPhone = async (phoneNumber) => {
    if (!phoneNumber || phoneNumber.length < 10 || isLoadingCustomer) return;
    
    try {
      setIsLoadingCustomer(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/customers/phone/${phoneNumber}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setBillData(prev => ({
          ...prev,
          tokenNo: result.data.TokenNo,
          customerName: result.data.CustomerName,
          couponCode: result.data.CouponCode || null,
        }));
        setCustomerFetched(true);
        
        // Fetch available scheme credit and coupon
        await fetchAvailableSchemeCredit(result.data.TokenNo);
        await fetchCustomerCoupon(result.data.TokenNo);
        
        toast.success("Customer details loaded!");
        
        // Auto-apply scheme credit and coupon after a short delay to ensure calculations are updated
        setTimeout(() => {
          autoApplySchemeCredit();
          autoApplyCoupon();
        }, 100);
      } else {
        toast.error("Customer not found with this phone number");
        setCustomerFetched(false);
        setAvailableSchemeCredit(0);
        setBillData(prev => ({
          ...prev,
          tokenNo: "",
          customerName: "",
        }));
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      toast.error("Failed to fetch customer details");
      setCustomerFetched(false);
      setAvailableSchemeCredit(0);
    } finally {
      setIsLoadingCustomer(false);
    }
  };

  // Handle Token No change and fetch
  const handleTokenNoChange = (e) => {
    const value = e.target.value;
    setBillData(prev => ({ ...prev, tokenNo: value }));
    setActiveSearchField('token');
    
    // Clear customer data if token is cleared
    if (!value) {
      setCustomerFetched(false);
      setIsLoadingCustomer(false);
      lastCalculatedCouponRef.current = 0;
      setAppliedCoupon(0);
      setAvailableCoupon(0);
      setCustomerSuggestions([]);
      setIsSuggestionsOpen(false);
      setBillData(prev => ({ ...prev, tokenNo: "", customerName: "", contact: "" }));
      return;
    }

    // Trigger suggestions
    clearTimeout(suggestionsDebounceRef.current);
    suggestionsDebounceRef.current = setTimeout(() => searchCustomers(value), 250);
  };

  // Handle Name change (new — drives suggestions)
  const handleNameChange = (e) => {
    const value = e.target.value;
    setBillData(prev => ({ ...prev, customerName: value }));
    setActiveSearchField('name');

    if (!value) {
      setCustomerSuggestions([]);
      setIsSuggestionsOpen(false);
      return;
    }
    clearTimeout(suggestionsDebounceRef.current);
    suggestionsDebounceRef.current = setTimeout(() => searchCustomers(value), 250);
  };

  // Handle Phone change
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setBillData(prev => ({ ...prev, contact: value }));
    setActiveSearchField('phone');
    
    // Clear customer data if phone is cleared
    if (!value) {
      setCustomerFetched(false);
      setIsLoadingCustomer(false);
      lastCalculatedCouponRef.current = 0;
      setAppliedCoupon(0);
      setAvailableCoupon(0);
      setCustomerSuggestions([]);
      setIsSuggestionsOpen(false);
      setBillData(prev => ({ ...prev, tokenNo: "", customerName: "", contact: "" }));
      return;
    }

    // Trigger suggestions for phone
    clearTimeout(suggestionsDebounceRef.current);
    suggestionsDebounceRef.current = setTimeout(() => searchCustomers(value), 250);
  };

  // Handle Token No submit (button click or Enter key)
  const handleTokenNoSubmit = () => {
    if (billData.tokenNo && !customerFetched && !isLoadingCustomer) {
      fetchCustomerByToken(billData.tokenNo);
    }
  };

  // Handle Phone Number submit (button click or Enter key)
  const handlePhoneSubmit = () => {
    if (!billData.contact) {
      return;
    }
    
    // Validate phone number length
    if (billData.contact.length < 10) {
      toast.error("Phone number must be at least 10 digits");
      return;
    }
    
    if (!customerFetched && !isLoadingCustomer) {
      fetchCustomerByPhone(billData.contact);
    }
  };

  // Keyboard navigation for suggestion dropdown (shared across all 3 fields)
  const handleSuggestionKeyDown = (e) => {
    if (!isSuggestionsOpen || customerSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % customerSuggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev <= 0 ? customerSuggestions.length - 1 : prev - 1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      selectCustomerSuggestion(customerSuggestions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsSuggestionsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  // Handle Enter key press for Token No
  const handleTokenNoKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTokenNoSubmit();
    }
  };

  // Handle Enter key press for Phone Number
  const handlePhoneKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handlePhoneSubmit();
    }
  };

  // Handle Token No blur (when user finishes typing)
  const handleTokenNoBlur = () => {
    if (billData.tokenNo && !customerFetched) {
      fetchCustomerByToken(billData.tokenNo);
    }
  };

  // Handle Phone Number blur
  const handlePhoneBlur = () => {
    if (billData.contact && !customerFetched) {
      fetchCustomerByPhone(billData.contact);
    }
  };

  const handleItemChange = (itemId, field, value) => {
    setItems(items.map((item) => (item.id === itemId ? { ...item, [field]: value } : item)));
  };

  const handleSizeChange = (itemId, sizeIndex, field, value) => {
    setItems(
      items.map((item) => {
        if (item.id === itemId) {
          const newSizes = [...item.sizes];
          newSizes[sizeIndex] = { ...newSizes[sizeIndex], [field]: value };
          return { ...item, sizes: newSizes };
        }
        return item;
      })
    );
  };

  const handleCustomizationChange = (itemId, sizeIndex, type, customIndex, field, value) => {
    setItems(
      items.map((item) => {
        if (item.id === itemId) {
          const newSizes = [...item.sizes];
          const newCustomizations = [...newSizes[sizeIndex][type]];
          newCustomizations[customIndex] = { ...newCustomizations[customIndex], [field]: value };
          newSizes[sizeIndex] = { ...newSizes[sizeIndex], [type]: newCustomizations };
          return { ...item, sizes: newSizes };
        }
        return item;
      })
    );
  };

  const addSize = (itemId) => {
    // Get the selected product details for this item
    const selectedProduct = selectedProducts[itemId];
    const unitPrice = selectedProduct?.Price || "";
    
    setItems(
      items.map((item) =>
        item.id === itemId
          ? { 
              ...item, 
              sizes: [
                ...item.sizes, 
                { 
                  size: "", 
                  quantity: "1", 
                  unitPrice: unitPrice,
                  discount: "",
                  alterations: [{ description: "", cost: "" }],
                  stitching: [{ description: "", cost: "" }]
                }
              ] 
            }
          : item
      )
    );
  };

  const removeSize = (itemId, sizeIndex) => {
    setItems(
      items.map((item) =>
        item.id === itemId ? { ...item, sizes: item.sizes.filter((_, i) => i !== sizeIndex) } : item
      )
    );
  };

  const addCustomization = (itemId, sizeIndex, type) => {
    setItems(
      items.map((item) => {
        if (item.id === itemId) {
          const newSizes = [...item.sizes];
          newSizes[sizeIndex] = {
            ...newSizes[sizeIndex],
            [type]: [...newSizes[sizeIndex][type], { description: "", cost: "", materialId: "", quantityUsed: "" }]
          };
          return { ...item, sizes: newSizes };
        }
        return item;
      })
    );
  };

  const removeCustomization = (itemId, sizeIndex, type, customIndex) => {
    setItems(
      items.map((item) => {
        if (item.id === itemId) {
          const newSizes = [...item.sizes];
          newSizes[sizeIndex] = {
            ...newSizes[sizeIndex],
            [type]: newSizes[sizeIndex][type].filter((_, i) => i !== customIndex)
          };
          return { ...item, sizes: newSizes };
        }
        return item;
      })
    );
  };

  const validateCurrentItem = () => {
    const lastItem = items[items.length - 1];
    if (!lastItem.productCode) {
      setErrors({ general: "Please enter Product Code before adding new item" });
      return false;
    }
    if (lastItem.sizes.some((s) => !s.size || !s.unitPrice)) {
      setErrors({ general: "Please fill all size details before adding new item" });
      return false;
    }
    setErrors({});
    return true;
  };

  const addNewItem = () => {
    if (!validateCurrentItem()) return;

    setItems([
      ...items,
      {
        id: Date.now(),
        productCode: "",
        sizes: [
          { 
            size: "", 
            quantity: "", 
            unitPrice: "",
            discount: "",
            alterations: [{ description: "", cost: "" }],
            stitching: [{ description: "", cost: "" }]
          }
        ],
      },
    ]);
  };

  const removeItem = (itemId) => {
    if (items.length > 1) {
      setItems(items.filter((item) => item.id !== itemId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateCurrentItem()) return;

    if (!customerFetched || !billData.tokenNo || !billData.customerName || !billData.contact) {
      setErrors({ general: "Please select a customer from the database" });
      toast.error("Please select a customer from the database");
      return;
    }

    if (!billData.dueDate) {
      setErrors({ general: "Due date is required" });
      toast.error("Due date is required");
      return;
    }

    const billPayload = {
      tokenNo: billData.tokenNo,
      customerName: billData.customerName,
      contact: billData.contact,
      advance: billData.advance,
      schemeCreditApplied: appliedSchemeCredit || 0,
      couponDiscount: appliedCoupon || 0,
      wasCustomizationGood: billData.wasCustomizationGood,
      orderDescription: billData.orderDescription,
      dueDate: billData.dueDate,
      taxRate: 0,
      discount: billData.discountType === 'bill' ? parseFloat(calculations.totalDiscount) : 0,
      discountPercent: billData.discountType === 'bill' ? (billData.billDiscountPercent || 0) : 0,
      discountType: billData.discountType,
      billedBy,
      items: items.map((item) => ({
        productCode: item.productCode,
        sizes: item.sizes.map(size => ({
          ...size,
          // Only include item discount if discount type is 'item'
          discount: billData.discountType === 'item' ? (size.discount || 0) : 0,
          alterations: size.alterations.filter((a) => a.description && a.description.trim() !== ''),
          stitching: size.stitching.filter((s) => s.description && s.description.trim() !== ''),
        })),
      })),
      ...calculations,
    };

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/bills/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(billPayload),
      });

      const data = await response.json();

      if (response.ok) {
        // Update the coupon code field with the generated code
        if (data.couponCode) {
          setBillData(prev => ({ ...prev, couponCode: data.couponCode }));
        }
        
        // If scheme credit was auto-applied, apply it to the newly created bill
        if (appliedSchemeCredit > 0) {
          try {
            // Get the first available scheme for this customer
            const schemeResponse = await fetch(`${API_BASE_URL}/api/schemes/customer/${billData.tokenNo}/available-credit`, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            });

            if (schemeResponse.ok) {
              const schemeData = await schemeResponse.json();
              const firstScheme = schemeData.schemes && schemeData.schemes.length > 0 ? schemeData.schemes[0] : null;
              
              if (firstScheme) {
                const creditResponse = await fetch(`${API_BASE_URL}/api/schemes/credit/apply`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    schemeId: firstScheme.SchemeId,
                    billId: data.billId,
                    amountToApply: appliedSchemeCredit,
                    notes: `Auto-applied to Bill #${data.billNumber}`
                  }),
                });

                if (creditResponse.ok) {
                  toast.success(`Bill created successfully! Bill Number: ${data.billNumber}${data.couponCode ? `, Customer Coupon: ${data.couponCode}` : ''}. Scheme credit of ₹${appliedSchemeCredit.toFixed(2)} auto-applied.`);
                } else {
                  toast.warning(`Bill created (${data.billNumber}) but failed to apply scheme credit. Please apply manually.`);
                }
              } else {
                toast.success(`Bill created successfully! Bill Number: ${data.billNumber}${data.couponCode ? `, Customer Coupon: ${data.couponCode}` : ''}`);
              }
            } else {
              toast.warning(`Bill created (${data.billNumber}) but failed to apply scheme credit. Please apply manually.`);
            }
          } catch (creditError) {
            console.error("Error applying scheme credit:", creditError);
            toast.warning(`Bill created (${data.billNumber}) but failed to apply scheme credit. Please apply manually.`);
          }
        } else {
          toast.success(`Bill created successfully! Bill Number: ${data.billNumber}${data.couponCode ? `, Customer Coupon: ${data.couponCode}` : ''}`);
        }
        
        // Trigger data refresh events
        setTimeout(() => {
          window.dispatchEvent(new Event('refreshCustomerData'));
          window.dispatchEvent(new Event('refreshDashboardData'));
        }, 500);
        
        navigate("/bills");
      } else {
        const errorMsg = data.message || "Failed to create bill";
        toast.error(errorMsg);
        setErrors({ general: errorMsg });
      }
    } catch (error) {
      console.error("Error creating bill:", error);
      const errorMsg = "Failed to create bill. Please try again.";
      toast.error(errorMsg);
      setErrors({ general: errorMsg });
    }
  };

  return (
    <AppLayout>
      <Box
        sx={{
          minHeight: "calc(100vh - 80px)",
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          p: { xs: 2, md: 4 },
        }}
      >
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/bills")}
          sx={{
            color: "#C4A574",
            mb: 3,
            fontSize: "0.95rem",
            "&:hover": { bgcolor: "rgba(196, 165, 116, 0.1)" },
          }}
        >
          Back to Bills
        </Button>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 500,
            letterSpacing: 3,
            color: "#BC965E",
            textAlign: "center",
            mb: 4,
            textDecoration: "underline",
            textUnderlineOffset: 12,
            textDecorationThickness: 2,
          }}
        >
          CREATE BILL
        </Typography>

        {/* Main Content Container */}
        <Box sx={{ maxWidth: 1300, mx: "auto" }}>
          {/* Error Alert */}
          {errors.general && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                backgroundColor: "rgba(139, 0, 0, 0.2)",
                color: "#fff",
                "& .MuiAlert-icon": { color: "#ff6b6b" }
              }} 
              onClose={() => setErrors({})}
            >
              {errors.general}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {/* CUSTOMER DETAILS SECTION */}
            <Box
              sx={{
                bgcolor: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(196, 165, 116, 0.2)",
                borderRadius: 2,
                p: 3,
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#fff",
                  mb: 2.5,
                  fontSize: "1rem",
                  letterSpacing: 0.5,
                }}
              >
                Customer Details
              </Typography>
              
              {customerFetched && (
                <Alert 
                  severity="success" 
                  sx={{ 
                    mb: 2,
                    backgroundColor: "rgba(0, 100, 0, 0.2)",
                    color: "#fff",
                    "& .MuiAlert-icon": { color: "#4caf50" }
                  }}
                  action={
                    <Button
                      size="small"
                      onClick={() => {
                        setCustomerFetched(false);
                        setIsLoadingCustomer(false);
                        setAppliedCoupon(0);
                        setAvailableCoupon(0);
                        setAppliedSchemeCredit(0);
                        setAvailableSchemeCredit(0);
                        lastCalculatedCouponRef.current = 0;
                        setCustomerSuggestions([]);
                        setIsSuggestionsOpen(false);
                        setBillData(prev => ({ ...prev, tokenNo: "", customerName: "", contact: "", couponCode: "" }));
                      }}
                      sx={{ color: "#fff", fontWeight: 700, textDecoration: "underline" }}
                    >
                      Change
                    </Button>
                  }
                >
                  Customer details loaded from database
                </Alert>
              )}
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.9rem" }}>
                    Token No *
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      name="tokenNo"
                      placeholder="Token No"
                      value={billData.tokenNo}
                      onChange={handleTokenNoChange}
                      onBlur={() => { setTimeout(() => setIsSuggestionsOpen(false), 200); handleTokenNoBlur(); }}
                      onFocus={() => { setActiveSearchField('token'); if (customerSuggestions.length > 0) setIsSuggestionsOpen(true); }}
                      onKeyDown={handleSuggestionKeyDown}
                      onKeyPress={handleTokenNoKeyPress}
                      fullWidth
                      required
                      disabled={customerFetched}
                      InputProps={{
                        endAdornment: !customerFetched && billData.tokenNo && (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleTokenNoSubmit}
                              disabled={!billData.tokenNo || isLoadingCustomer}
                              sx={{ color: "#C4A574", "&:hover": { bgcolor: "rgba(196, 165, 116, 0.1)" }, "&:disabled": { color: "#666" } }}
                            >
                              <CheckIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ ...TextFieldStyle, ...(customerFetched && { "& .MuiOutlinedInput-root": { ...TextFieldStyle["& .MuiOutlinedInput-root"], backgroundColor: "rgba(245, 245, 245, 0.1)" } }) }}
                    />
                    {isSuggestionsOpen && activeSearchField === 'token' && customerSuggestions.length > 0 && (
                      <SuggestionDropdown suggestions={customerSuggestions} onSelect={selectCustomerSuggestion} highlightedIndex={highlightedIndex} />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.9rem" }}>
                    Customer Name *
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      name="customerName"
                      placeholder="Search by name..."
                      value={billData.customerName}
                      onChange={handleNameChange}
                      onBlur={() => setTimeout(() => setIsSuggestionsOpen(false), 200)}
                      onFocus={() => { setActiveSearchField('name'); if (customerSuggestions.length > 0) setIsSuggestionsOpen(true); }}
                      onKeyDown={handleSuggestionKeyDown}
                      fullWidth
                      required
                      disabled={customerFetched}
                      sx={{ ...TextFieldStyle, ...(customerFetched && { "& .MuiOutlinedInput-root": { ...TextFieldStyle["& .MuiOutlinedInput-root"], backgroundColor: "rgba(245, 245, 245, 0.1)" } }) }}
                    />
                    {isSuggestionsOpen && activeSearchField === 'name' && customerSuggestions.length > 0 && (
                      <SuggestionDropdown suggestions={customerSuggestions} onSelect={selectCustomerSuggestion} highlightedIndex={highlightedIndex} />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.9rem" }}>
                    Phone Number *
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      name="contact"
                      placeholder="Search by phone..."
                      value={billData.contact}
                      onChange={handlePhoneChange}
                      onBlur={() => { setTimeout(() => setIsSuggestionsOpen(false), 200); handlePhoneBlur(); }}
                      onFocus={() => { setActiveSearchField('phone'); if (customerSuggestions.length > 0) setIsSuggestionsOpen(true); }}
                      onKeyDown={handleSuggestionKeyDown}
                      onKeyPress={handlePhoneKeyPress}
                      fullWidth
                      required
                      disabled={customerFetched}
                      InputProps={{
                        endAdornment: !customerFetched && billData.contact && (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handlePhoneSubmit}
                              disabled={!billData.contact || isLoadingCustomer}
                              sx={{ color: "#C4A574", "&:hover": { bgcolor: "rgba(196, 165, 116, 0.1)" }, "&:disabled": { color: "#666" } }}
                            >
                              <CheckIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ ...TextFieldStyle, ...(customerFetched && { "& .MuiOutlinedInput-root": { ...TextFieldStyle["& .MuiOutlinedInput-root"], backgroundColor: "rgba(245, 245, 245, 0.1)" } }) }}
                    />
                    {isSuggestionsOpen && activeSearchField === 'phone' && customerSuggestions.length > 0 && (
                      <SuggestionDropdown suggestions={customerSuggestions} onSelect={selectCustomerSuggestion} highlightedIndex={highlightedIndex} />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.9rem" }}>
                    Billed By
                  </Typography>
                  <TextField 
                    value={billedBy} 
                    fullWidth 
                    disabled 
                    sx={{
                      ...TextFieldStyle,
                      "& .MuiInputBase-input": {
                        color: "#fff",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.9rem" }}>
                    Customer Coupon Code
                  </Typography>
                  <TextField
                    value={billData.couponCode || "Will be updated after bill creation"}
                    fullWidth
                    disabled
                    sx={{
                      ...TextFieldStyle,
                      "& .MuiInputBase-input": {
                        color: billData.couponCode ? "#fff" : "#999",
                        fontFamily: billData.couponCode ? "monospace" : "inherit",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.9rem" }}>
                    Advance Amount (₹)
                  </Typography>
                  <TextField
                    name="advance"
                    placeholder="Enter advance amount"
                    value={billData.advance}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        handleBillDataChange(e);
                      }
                    }}
                    fullWidth
                    sx={TextFieldStyle}
                  />
                </Grid>
                
                {/* Scheme Credit Section */}
                {billData.tokenNo && customerFetched && (
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.9rem" }}>
                      Scheme Credit
                    </Typography>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setSchemeCreditDialogOpen(true)}
                      sx={{
                        height: '56px',
                        borderColor: '#9c27b0',
                        color: '#9c27b0',
                        textTransform: 'none',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: '#7b1fa2',
                          bgcolor: 'rgba(156, 39, 176, 0.1)'
                        }
                      }}
                    >
                      {appliedSchemeCredit > 0 
                        ? `Applied: ₹${appliedSchemeCredit.toFixed(2)}`
                        : 'Apply Scheme Credit'}
                    </Button>
                    {availableSchemeCredit > 0 && appliedSchemeCredit === 0 && (
                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: "#888" }}>
                        Available: ₹{availableSchemeCredit.toFixed(2)}
                      </Typography>
                    )}
                  </Grid>
                )}
                
                <Grid item xs={12} md={6}>
                  <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.9rem" }}>
                    Due Date *
                  </Typography>
                  <TextField
                    name="dueDate"
                    type="date"
                    value={billData.dueDate}
                    onChange={handleBillDataChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                    error={!billData.dueDate && errors.general}
                    sx={TextFieldStyle}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.9rem" }}>
                    Order Description
                  </Typography>
                  <TextField
                    name="orderDescription"
                    value={billData.orderDescription}
                    onChange={handleBillDataChange}
                    placeholder="Add any comments or special instructions for this order"
                    fullWidth
                    multiline
                    rows={3}
                    sx={TextFieldStyle}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* ITEMS SECTION */}
            {items.map((item, itemIndex) => (
              <Box
                key={item.id}
                sx={{
                  bgcolor: "rgba(0, 0, 0, 0.3)",
                  border: "1px solid rgba(196, 165, 116, 0.2)",
                  borderRadius: 2,
                  p: 3,
                  mb: 3,
                }}
              >
                {/* Item Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#fff",
                      fontSize: "1rem",
                      letterSpacing: 0.5,
                    }}
                  >
                    Item #{itemIndex + 1}
                  </Typography>
                  {items.length > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => removeItem(item.id)}
                      sx={{ color: "#ff6b6b" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                {/* Product Search */}
                <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.9rem" }}>
                  Product *
                </Typography>
                <Autocomplete
                  options={productSearch[item.id] || []}
                  getOptionLabel={(option) => `${option.ProductCode} - ${option.ProductName}`}
                  onChange={(e, newValue) => handleProductSelect(item.id, newValue)}
                  onInputChange={(e, value) => handleProductSearch(item.id, value)}
                  filterOptions={(x) => x}
                  noOptionsText="Type at least 2 characters to search..."
                  freeSolo
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      placeholder="Search by code, name, color, size, or supplier..."
                      required 
                      fullWidth 
                      sx={TextFieldStyle}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps} sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 2,
                        bgcolor: "rgba(0, 0, 0, 0.8)",
                        color: "#fff",
                        "&:hover": {
                          bgcolor: "rgba(196, 165, 116, 0.1)",
                        }
                      }}>
                        <ListItemAvatar>
                          {option.Photo ? (
                            <Avatar
                            src={`${API_BASE_URL}${option.Photo}`}
                            variant="rounded"
                            sx={{ width: 50, height: 50, cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImagePreview(`${API_BASE_URL}${option.Photo}`);
                            }}
                          />
                        ) : (
                          <Avatar variant="rounded" sx={{ width: 50, height: 50, bgcolor: "rgba(196, 165, 116, 0.2)" }}>
                            <ImageIcon sx={{ color: "#C4A574" }} />
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${option.ProductCode} - ${option.ProductName}`}
                        secondary={`${option.Category} - ${option.Color || 'N/A'} - ₹${option.Price}${option.PurchaseFrom ? ` - Supplier: ${option.PurchaseFrom}` : ''}`}
                        sx={{
                          "& .MuiListItemText-primary": { color: "#fff" },
                          "& .MuiListItemText-secondary": { color: "rgba(255, 255, 255, 0.7)" }
                        }}
                      />
                      {option.Photo && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImagePreview(`${API_BASE_URL}${option.Photo}`);
                          }}
                          sx={{ color: "#C4A574" }}
                        >
                          <ZoomInIcon />
                        </IconButton>
                      )}
                    </Box>
                    );
                  }}
                  sx={{ mb: 3 }}
                />

                {/* SIZES WITH CUSTOMIZATIONS */}
                <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 2, fontSize: "0.9rem" }}>
                  Sizes
                </Typography>
                {item.sizes.map((sizeItem, sizeIndex) => (
                  <Box key={sizeIndex} sx={{ 
                    mb: 3, 
                    p: 2, 
                    border: "1px solid rgba(196, 165, 116, 0.2)", 
                    borderRadius: 2,
                    bgcolor: "rgba(0, 0, 0, 0.2)"
                  }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#fff" }}>
                        Size #{sizeIndex + 1}
                      </Typography>
                      {item.sizes.length > 1 && (
                        <IconButton size="small" onClick={() => removeSize(item.id, sizeIndex)} sx={{ color: "#ff6b6b" }}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={4}>
                        <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.8rem" }}>
                          Size
                        </Typography>
                        <TextField
                          select
                          value={sizeItem.size}
                          onChange={(e) => handleSizeChange(item.id, sizeIndex, "size", e.target.value)}
                          fullWidth
                          required
                          sx={TextFieldStyle}
                        >
                          {(() => {
                            // Get the selected product details
                            const selectedProduct = selectedProducts[item.id];
                            const availableSizes = selectedProduct?.sizes?.map(s => s.Size) || sizes;
                            
                            return availableSizes.map((size) => (
                              <MenuItem key={size} value={size} sx={{ color: "#000" }}>
                                {size}
                              </MenuItem>
                            ));
                          })()}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.8rem" }}>
                          Quantity
                        </Typography>
                        <TextField
                          placeholder="Qty"
                          type="text"
                          value={sizeItem.quantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\d+$/.test(value)) {
                              handleSizeChange(item.id, sizeIndex, "quantity", value);
                            }
                          }}
                          fullWidth
                          sx={TextFieldStyle}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.8rem" }}>
                          Unit Price (₹)
                        </Typography>
                        <TextField
                          placeholder="Price"
                          type="text"
                          value={sizeItem.unitPrice}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              handleSizeChange(item.id, sizeIndex, "unitPrice", value);
                            }
                          }}
                          fullWidth
                          required
                          sx={TextFieldStyle}
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, fontSize: "0.8rem" }}>
                          D (₹)
                        </Typography>
                        <TextField
                          placeholder="D"
                          type="text"
                          value={sizeItem.discount}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              handleSizeChange(item.id, sizeIndex, "discount", value);
                            }
                          }}
                          fullWidth
                          disabled={billData.discountType === 'bill'}
                          sx={TextFieldStyle}
                        />
                      </Grid>
                    </Grid>

                    {/* ALTERATIONS FOR THIS SIZE */}
                    <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, mt: 2, fontSize: "0.8rem" }}>
                      Alterations (Optional)
                    </Typography>
                    {sizeItem.alterations.map((alt, altIndex) => (
                      <Grid container spacing={2} key={altIndex} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            placeholder="E.g., Sleeve shortening"
                            value={alt.description}
                            onChange={(e) =>
                              handleCustomizationChange(item.id, sizeIndex, "alterations", altIndex, "description", e.target.value)
                            }
                            fullWidth
                            size="small"
                            sx={TextFieldStyle}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            placeholder="Cost"
                            type="text"
                            value={alt.cost}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                handleCustomizationChange(item.id, sizeIndex, "alterations", altIndex, "cost", value);
                              }
                            }}
                            fullWidth
                            size="small"
                            sx={TextFieldStyle}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            placeholder="Material (Optional)"
                            value={alt.materialId || ""}
                            onChange={(e) =>
                              handleCustomizationChange(item.id, sizeIndex, "alterations", altIndex, "materialId", e.target.value)
                            }
                            fullWidth
                            size="small"
                            sx={TextFieldStyle}
                          >
                            <MenuItem value="" sx={{ color: "#000" }}>None</MenuItem>
                            {rawMaterials.map((material) => (
                              <MenuItem key={material.MaterialId} value={material.MaterialId} sx={{ color: "#000" }}>
                                {material.MaterialName} ({material.QuantityInStock} {material.Unit})
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={1.5}>
                          <TextField
                            placeholder="0"
                            type="text"
                            value={alt.quantityUsed || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                handleCustomizationChange(item.id, sizeIndex, "alterations", altIndex, "quantityUsed", value);
                              }
                            }}
                            fullWidth
                            size="small"
                            disabled={!alt.materialId}
                            sx={TextFieldStyle}
                          />
                        </Grid>
                        <Grid item xs={12} sm={0.5}>
                          <IconButton size="small" onClick={() => removeCustomization(item.id, sizeIndex, "alterations", altIndex)} sx={{ color: "#ff6b6b" }}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addCustomization(item.id, sizeIndex, "alterations")}
                      size="small"
                      sx={{ 
                        mb: 2,
                        color: "#C4A574",
                        borderColor: "#C4A574",
                        "&:hover": { 
                          bgcolor: "rgba(196, 165, 116, 0.1)",
                          borderColor: "#C4A574"
                        }
                      }}
                    >
                      Add Alteration
                    </Button>

                    {/* STITCHING FOR THIS SIZE */}
                    <Typography sx={{ color: "#C4A574", fontWeight: 700, mb: 1, mt: 2, fontSize: "0.8rem" }}>
                      Stitching (Optional)
                    </Typography>
                    {sizeItem.stitching.map((stitch, stitchIndex) => (
                      <Grid container spacing={2} key={stitchIndex} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            placeholder="E.g., Custom fitting"
                            value={stitch.description}
                            onChange={(e) =>
                              handleCustomizationChange(item.id, sizeIndex, "stitching", stitchIndex, "description", e.target.value)
                            }
                            fullWidth
                            size="small"
                            sx={TextFieldStyle}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            placeholder="Cost"
                            type="text"
                            value={stitch.cost}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                handleCustomizationChange(item.id, sizeIndex, "stitching", stitchIndex, "cost", value);
                              }
                            }}
                            fullWidth
                            size="small"
                            sx={TextFieldStyle}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            placeholder="Material (Optional)"
                            value={stitch.materialId || ""}
                            onChange={(e) =>
                              handleCustomizationChange(item.id, sizeIndex, "stitching", stitchIndex, "materialId", e.target.value)
                            }
                            fullWidth
                            size="small"
                            sx={TextFieldStyle}
                          >
                            <MenuItem value="" sx={{ color: "#000" }}>None</MenuItem>
                            {rawMaterials.map((material) => (
                              <MenuItem key={material.MaterialId} value={material.MaterialId} sx={{ color: "#000" }}>
                                {material.MaterialName} ({material.QuantityInStock} {material.Unit})
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={1.5}>
                          <TextField
                            placeholder="0"
                            type="text"
                            value={stitch.quantityUsed || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                handleCustomizationChange(item.id, sizeIndex, "stitching", stitchIndex, "quantityUsed", value);
                              }
                            }}
                            fullWidth
                            size="small"
                            disabled={!stitch.materialId}
                            sx={TextFieldStyle}
                          />
                        </Grid>
                        <Grid item xs={12} sm={0.5}>
                          <IconButton size="small" onClick={() => removeCustomization(item.id, sizeIndex, "stitching", stitchIndex)} sx={{ color: "#ff6b6b" }}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addCustomization(item.id, sizeIndex, "stitching")}
                      size="small"
                      sx={{ 
                        mb: 2,
                        color: "#C4A574",
                        borderColor: "#C4A574",
                        "&:hover": { 
                          bgcolor: "rgba(196, 165, 116, 0.1)",
                          borderColor: "#C4A574"
                        }
                      }}
                    >
                      Add Stitching
                    </Button>
                  </Box>
                ))}
                <Button 
                  startIcon={<AddIcon />} 
                  onClick={() => addSize(item.id)} 
                  size="small" 
                  sx={{ 
                    mb: 2,
                    color: "#C4A574",
                    borderColor: "#C4A574",
                    "&:hover": { 
                      bgcolor: "rgba(196, 165, 116, 0.1)",
                      borderColor: "#C4A574"
                    }
                  }}
                >
                  Add Another Size
                </Button>

              </Box>
            ))}

            {/* ADD ITEM BUTTON */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addNewItem}
              fullWidth
              sx={{ 
                mb: 3, 
                py: 1.5, 
                borderWidth: 2, 
                borderColor: "#C4A574",
                color: "#C4A574",
                "&:hover": { 
                  borderWidth: 2,
                  borderColor: "#C4A574",
                  bgcolor: "rgba(196, 165, 116, 0.1)"
                }
              }}
            >
              Add Another Item
            </Button>

            {/* CUSTOMIZATION FEEDBACK */}
            <Box
              sx={{
                bgcolor: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(196, 165, 116, 0.2)",
                borderRadius: 2,
                p: 3,
                mb: 3,
              }}
            >
              <FormControl component="fieldset">
                <FormLabel sx={{ color: "#C4A574", fontWeight: 700, mb: 1 }}>
                  Was customization good?
                </FormLabel>
                <RadioGroup
                  row
                  name="wasCustomizationGood"
                  value={billData.wasCustomizationGood}
                  onChange={handleBillDataChange}
                >
                  <FormControlLabel 
                    value="Yes" 
                    control={<Radio sx={{ color: "#C4A574", "&.Mui-checked": { color: "#C4A574" } }} />} 
                    label={<Typography sx={{ color: "#fff" }}>Yes</Typography>}
                  />
                  <FormControlLabel 
                    value="No" 
                    control={<Radio sx={{ color: "#C4A574", "&.Mui-checked": { color: "#C4A574" } }} />} 
                    label={<Typography sx={{ color: "#fff" }}>No</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* DISCOUNT TYPE SELECTOR */}
            <Box
              sx={{
                bgcolor: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(196, 165, 116, 0.2)",
                borderRadius: 2,
                p: 3,
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#fff",
                  mb: 2.5,
                  fontSize: "1rem",
                  letterSpacing: 0.5,
                }}
              >
                D Options
              </Typography>
              <FormControl component="fieldset" fullWidth>
                <FormLabel sx={{ color: "#C4A574", fontWeight: 700, mb: 1 }}>
                  Apply D On:
                </FormLabel>
                <RadioGroup
                  row
                  name="discountType"
                  value={billData.discountType}
                  onChange={handleBillDataChange}
                  sx={{ mb: 2 }}
                >
                  <FormControlLabel 
                    value="item" 
                    control={<Radio sx={{ color: "#C4A574", "&.Mui-checked": { color: "#C4A574" } }} />} 
                    label={<Typography sx={{ color: "#fff" }}>Individual Items</Typography>}
                  />
                  <FormControlLabel 
                    value="bill" 
                    control={<Radio sx={{ color: "#C4A574", "&.Mui-checked": { color: "#C4A574" } }} />} 
                    label={<Typography sx={{ color: "#fff" }}>Total Bill</Typography>}
                  />
                </RadioGroup>
                
                {billData.discountType === 'bill' && (
                  <TextField
                    name="billDiscountPercent"
                    placeholder="Enter d percentage (0-100)"
                    type="text"
                    value={billData.billDiscountPercent}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow numbers between 0-100 with decimals
                      if (value === "" || (/^\d*\.?\d*$/.test(value) && parseFloat(value) <= 100)) {
                        handleBillDataChange(e);
                      }
                    }}
                    fullWidth
                    sx={TextFieldStyle}
                  />
                )}
                
                {billData.discountType === 'item' && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mt: 1,
                      backgroundColor: "rgba(33, 150, 243, 0.1)",
                      color: "#fff",
                      "& .MuiAlert-icon": { color: "#2196f3" }
                    }}
                  >
                    Add d to individual items in the "D (₹)" field for each size
                  </Alert>
                )}
              </FormControl>
            </Box>

            {/* BILL SUMMARY */}
            <Box
              sx={{
                bgcolor: "rgba(0, 0, 0, 0.4)",
                border: "2px solid rgba(196, 165, 116, 0.3)",
                borderRadius: 2,
                p: 3,
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#C4A574",
                  mb: 3,
                  fontSize: "1.1rem",
                  letterSpacing: 0.5,
                  textAlign: "center",
                }}
              >
                Bill Summary
              </Typography>
              
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
                <Typography>SubTotal:</Typography>
                <Typography fontWeight={600}>₹ {calculations.subTotal}</Typography>
              </Box>
              
              {(parseFloat(calculations.totalAlterations) > 0 || parseFloat(calculations.totalStitching) > 0) && (
                <>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, color: "rgba(255, 255, 255, 0.7)", pl: 2 }}>
                    <Typography variant="body2">• Alterations:</Typography>
                    <Typography variant="body2">₹ {calculations.totalAlterations}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "rgba(255, 255, 255, 0.7)", pl: 2 }}>
                    <Typography variant="body2">• Stitching:</Typography>
                    <Typography variant="body2">₹ {calculations.totalStitching}</Typography>
                  </Box>
                </>
              )}
              
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
                <Typography>D {billData.discountType === 'bill' ? '(Bill Level)' : '(Item Level)'}:</Typography>
                <Typography fontWeight={600}>- ₹ {calculations.totalDiscount}</Typography>
              </Box>
              
              {appliedCoupon > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#ff9800" }}>
                  <Typography sx={{ fontWeight: 600 }}>Coupon Applied:</Typography>
                  <Typography sx={{ fontWeight: 600 }}>- ₹ {appliedCoupon.toFixed(2)}</Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 2, borderColor: "rgba(196, 165, 116, 0.2)" }} />
              
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
                <Typography variant="h6">Total Amount:</Typography>
                <Typography variant="h6" fontWeight={700}>₹ {calculations.totalAmount}</Typography>
              </Box>
              
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
                <Typography>Advance Paid:</Typography>
                <Typography>- ₹ {billData.advance || 0}</Typography>
              </Box>
              
              {appliedSchemeCredit > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#9c27b0" }}>
                  <Typography sx={{ fontWeight: 600 }}>Scheme Credit Applied:</Typography>
                  <Typography sx={{ fontWeight: 600 }}>- ₹ {appliedSchemeCredit.toFixed(2)}</Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 2, borderColor: "rgba(196, 165, 116, 0.2)" }} />
              
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: "#C4A574",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                }}
              >
                <Typography>Balance Due:</Typography>
                <Typography>₹ {calculations.balance}</Typography>
              </Box>
            </Box>

            {/* ACTION BUTTONS */}
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/bills")}
                sx={{
                  borderColor: "#C4A574",
                  color: "#C4A574",
                  fontWeight: 700,
                  px: 4,
                  py: 1.2,
                  "&:hover": {
                    bgcolor: "rgba(196, 165, 116, 0.1)",
                    borderColor: "#C4A574",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  bgcolor: "#C4A574",
                  color: "#000",
                  fontWeight: 700,
                  px: 4,
                  py: 1.2,
                  "&:hover": { bgcolor: "#B39565" },
                }}
              >
                Create Bill
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Decorative Fade */}
        <Box
          sx={{
            mt: 6,
            height: 60,
            background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.3))",
            borderRadius: 2,
          }}
        />
      </Box>

      {/* IMAGE PREVIEW DIALOG */}
      <Dialog 
        open={imagePreview.open} 
        onClose={closeImagePreview} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.9)",
            border: "1px solid rgba(196, 165, 116, 0.3)",
          }
        }}
      >
        <DialogTitle sx={{ color: "#C4A574", fontWeight: 700 }}>
          Product Image
          <IconButton
            onClick={closeImagePreview}
            sx={{ 
              position: "absolute", 
              right: 8, 
              top: 8,
              color: "#C4A574"
            }}
          >
            <DeleteIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <img
              src={imagePreview.url}
              alt="Product"
              style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* SCHEME CREDIT DIALOG */}
      <ApplySchemeCreditDialog
        open={schemeCreditDialogOpen}
        onClose={() => setSchemeCreditDialogOpen(false)}
        customerTokenNo={billData.tokenNo}
        billId={null} // No billId for create mode
        billAmount={calculations.totalAmount}
        onSuccess={handleSchemeCreditApplied}
        previewMode={true} // Preview mode for create bill
      />

    </AppLayout>
  );
}
